import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import GlobalHeader from "../../../../../components/GlobalHeader.jsx";
import Pagination from "../../../../../components/Pagination.jsx";
import TicketsTable from "../components/TicketsTable.jsx";
import "../css/TicketsEventList.css";
import { fetchMyEvents } from "../../../../../utils/api/organiserApi.js";

const ITEMS_PER_PAGE = 5;

const TicketsEventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[TicketsEventList] 🎟️ Fetching — API: fetchMyEvents()');
      const data = await fetchMyEvents();
      console.log('[TicketsEventList] ✅ Events loaded:', {
        columns: 'event_id/_id/id/EventID, event_name/Name/name',
        count: data?.length,
      });

      const mappedEvents = data.map(event => ({
        ...event,
        event_id: event._id || event.event_id || event.id || event.EventID,
        name: event.event_name || event.Name || event.name || 'Untitled Event'
      }));
      
      setEvents(mappedEvents);
    } catch (err) {
      setError(err?.message || "Failed to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEvents = useMemo(() => {
    if (!searchTerm) return events;
    const term = searchTerm.toLowerCase();
    return events.filter((event) => {
      const name = event?.name || event?.event_name || event?.Name || "";
      return name.toLowerCase().includes(term);
    });
  }, [events, searchTerm]);

  const handleViewTickets = (eventId) => {
    if (!eventId) return;
    navigate(`/ticketsrequest/${eventId}`);
  };

  const handleBack = () => {
    navigate('/requestcard');
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="tickets-event-list-page">
      <GlobalHeader
        title="Tickets"
        subtitle="View tickets purchased for your events"
        onBack={handleBack}
        logoutTo="/"
        onLogout={handleLogout}
      />

      <main className="tickets-event-list-main">
        <div className="tickets-event-list-search">
          <div className="tickets-event-list-search__inner">
            <input
              type="text"
              className="tickets-search-input"
              placeholder="Search events by name"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <span className="tickets-search-count">
              {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}
            </span>
          </div>
        </div>

        {loading && (
          <div className="tickets-loading">
            <div className="tickets-spinner" />
            <p>Loading your events...</p>
          </div>
        )}

        {!loading && error && (
          <div className="tickets-error">
            <h3>Unable to load events</h3>
            <p>{error}</p>
            <button className="tickets-retry-button" onClick={loadEvents}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <TicketsTable
              events={filteredEvents.slice(
                (currentPage - 1) * ITEMS_PER_PAGE,
                currentPage * ITEMS_PER_PAGE
              )}
              searchTerm={searchTerm}
              onViewTickets={handleViewTickets}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredEvents.length / ITEMS_PER_PAGE)}
              onPageChange={setCurrentPage}
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={filteredEvents.length}
            />
          </>
        )}
      </main>
    </div>
  );
};

export default TicketsEventList;
